import { mediaQueries, mediaQueryBySize, mobileScreen } from 'pricing-pages-ui-components/constants/Breakpoints';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * @description This hook is used to check if the current window matches a given media query string.
 *
 * @example
 * `useMedia('(max-width: 768px)')`
 * `useMedia('(min-width: 768px) and (max-width: 1024px)')`
 * `useMedia('${mediaQueries.phone} and ${mediaQueries.tablet}')`
 *
 * @param query - A raw CSS media query string.
 * @param initialState - The initial state of the media query.
 * @returns Whether the current window matches the media query.
 */
export function useMedia(query, initialState = false) {
  const subscribeMediaQuery = useCallback(onChange => {
    const mql = window.matchMedia(query);
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, [query]);
  return useSyncExternalStore(subscribeMediaQuery, () => window.matchMedia(query).matches, () => initialState);
}

/**
 * @see {@link pricing-pages-ui-components/constants/Breakpoints.mediaQueries}
 * @example
 * `useMediaByType('tabletPortraitLarge')`
 *
 * NOTE: Uses max-width media queries.
 */
export function useMediaByType(type, initialState = false) {
  const query = mediaQueries[type];
  return useMedia(query, initialState);
}

/**
 * @see {@link pricing-pages-ui-components/constants/Breakpoints.mediaQueryBySize}
 * @example
 * `useMediaBySize('lg')`
 *
 * NOTE: Uses min-width media queries.
 */
export function useMediaBySize(size, initialState = false) {
  const query = mediaQueryBySize[size];
  return useMedia(query, initialState);
}
export const useIsMobile = () => {
  return useMediaByType(mobileScreen);
};
export const usePrefersReducedMotion = () => {
  return useMedia('(prefers-reduced-motion: reduce)');
};